(function () {
  function debounce(fn, delay) {
    var timer = null;

    return function () {
      var context = this;
      var args = arguments;

      window.clearTimeout(timer);

      timer = window.setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  /**
   * 处理复制出来的组。
   * 复制组只负责视觉展示，不参与键盘操作和 Shopify 编辑器定位。
   */
  function prepareClonedGroup(group) {
    if (!group) return;

    group.setAttribute('aria-hidden', 'true');

    group
      .querySelectorAll(
        'a, button, input, select, textarea, [tabindex]'
      )
      .forEach(function (element) {
        element.setAttribute('tabindex', '-1');
      });

    group
      .querySelectorAll('[data-shopify-editor-block]')
      .forEach(function (element) {
        element.removeAttribute('data-shopify-editor-block');
      });

    group.removeAttribute('data-shopify-editor-block');
  }

  /**
   * 重新计算轨道：
   * 1. 保留第一组作为原始组；
   * 2. 删除之前生成的复制组；
   * 3. 自动复制到足以覆盖屏幕；
   * 4. 按第一组真实宽度进行循环。
   */
  function rebuildFeaturedAuthorities(section) {
    if (!section) return;

    var wrapper = section.querySelector(
      '.featured-authorities__wrapper'
    );

    var track = section.querySelector(
      '.featured-authorities__track'
    );

    if (!wrapper || !track) return;

    var groups = Array.prototype.slice.call(
      track.querySelectorAll(
        ':scope > .featured-authorities__group'
      )
    );

    if (!groups.length) return;

    var sourceGroup = groups[0];

    /* 暂停动画，防止重新计算时跳动 */
    track.classList.remove('is-ready');
    track.style.removeProperty('transform');
    track.style.setProperty('--fa-loop-distance', '0px');

    /*
     * 删除原 Liquid 输出的第二组，以及之前 JS 生成的组。
     * 永远以第一组作为唯一复制源。
     */
    groups.slice(1).forEach(function (group) {
      group.remove();
    });

    sourceGroup.removeAttribute('aria-hidden');

    var sourceRect = sourceGroup.getBoundingClientRect();
    var sourceWidth = sourceRect.width;
    var wrapperWidth = wrapper.getBoundingClientRect().width;

    if (!sourceWidth || sourceWidth <= 0 || !wrapperWidth) {
      return;
    }

    /*
     * 至少需要：
     * 可视区域宽度 + 两个完整原始组宽度。
     * 这样轨道移动一个完整组以后，右侧依然不会出现空白。
     */
    var requiredTrackWidth =
      wrapperWidth + sourceWidth * 2;

    var safetyCount = 0;
    var maximumCopies = 30;

    while (
      track.getBoundingClientRect().width <
        requiredTrackWidth &&
      safetyCount < maximumCopies
    ) {
      var clone = sourceGroup.cloneNode(true);

      prepareClonedGroup(clone);
      track.appendChild(clone);

      safetyCount += 1;
    }

    /*
     * 再额外复制一组作为安全缓冲。
     * 可以避免小数像素、字体宽度和浏览器缩放造成的细小空隙。
     */
    var safetyClone = sourceGroup.cloneNode(true);

    prepareClonedGroup(safetyClone);
    track.appendChild(safetyClone);

    /*
     * 动画每次只移动一个原始组的准确宽度。
     * 动画结束瞬间回到起点时，画面内容完全一致。
     */
    track.style.setProperty(
      '--fa-loop-distance',
      '-' + sourceWidth + 'px'
    );

    window.requestAnimationFrame(function () {
      /*
       * 强制浏览器确认动画起始状态。
       * 防止重新初始化时延续上一次 transform。
       */
      void track.offsetWidth;

      track.classList.add('is-ready');
    });
  }

  function activateItem(section, item) {
    if (!section || !item) return;

    var index = item.getAttribute('data-fa-index');

    if (index === null) return;

    section
      .querySelectorAll('.featured-authorities__item')
      .forEach(function (otherItem) {
        otherItem.classList.remove('is-active');
      });

    section
      .querySelectorAll(
        '[data-fa-index="' + index + '"]'
      )
      .forEach(function (sameItem) {
        sameItem.classList.add('is-active');
      });
  }

  /**
   * 使用事件代理。
   * 后续 JS 新复制出来的项目不需要重新逐个绑定事件。
   */
  function bindFeaturedAuthoritiesEvents(section) {
    if (!section) return;

    if (
      section.dataset.featuredAuthoritiesEventsBound ===
      'true'
    ) {
      return;
    }

    section.dataset.featuredAuthoritiesEventsBound = 'true';

    section.addEventListener('click', function (event) {
      var item = event.target.closest(
        '.featured-authorities__item'
      );

      if (!item || !section.contains(item)) return;

      activateItem(section, item);
    });

    section.addEventListener(
      'touchstart',
      function (event) {
        var item = event.target.closest(
          '.featured-authorities__item'
        );

        if (!item || !section.contains(item)) return;

        activateItem(section, item);
      },
      {
        passive: true
      }
    );
  }

  function initFeaturedAuthorities(section) {
    if (!section) return;

    bindFeaturedAuthoritiesEvents(section);

    var rebuild = debounce(function () {
      rebuildFeaturedAuthorities(section);
    }, 100);

    section._featuredAuthoritiesRebuild = rebuild;

    rebuildFeaturedAuthorities(section);

    /*
     * 监听模块尺寸变化。
     * 电脑端、移动端切换和主题编辑器宽度变化时自动重算。
     */
    if (
      'ResizeObserver' in window &&
      !section._featuredAuthoritiesResizeObserver
    ) {
      var wrapper = section.querySelector(
        '.featured-authorities__wrapper'
      );

      if (wrapper) {
        section._featuredAuthoritiesResizeObserver =
          new ResizeObserver(function () {
            rebuild();
          });

        section._featuredAuthoritiesResizeObserver.observe(
          wrapper
        );
      }
    }

    /*
     * 图片加载后重新测量。
     */
    section
      .querySelectorAll(
        '.featured-authorities__group:first-child img'
      )
      .forEach(function (image) {
        if (image.complete) return;

        image.addEventListener(
          'load',
          function () {
            rebuild();
          },
          {
            once: true
          }
        );
      });

    /*
     * 自定义字体加载完成后，文字实际宽度可能变化，
     * 因此需要重新测量一次。
     */
    if (
      document.fonts &&
      typeof document.fonts.ready !== 'undefined'
    ) {
      document.fonts.ready.then(function () {
        rebuild();
      });
    }
  }

  function initAllFeaturedAuthorities(root) {
    var searchRoot = root || document;

    var sections = [];

    if (
      searchRoot.matches &&
      searchRoot.matches('[data-featured-authorities]')
    ) {
      sections.push(searchRoot);
    }

    searchRoot
      .querySelectorAll('[data-featured-authorities]')
      .forEach(function (section) {
        sections.push(section);
      });

    sections.forEach(function (section) {
      if (
        section.dataset.featuredAuthoritiesInit === 'true'
      ) {
        if (section._featuredAuthoritiesRebuild) {
          section._featuredAuthoritiesRebuild();
        }

        return;
      }

      section.dataset.featuredAuthoritiesInit = 'true';
      initFeaturedAuthorities(section);
    });
  }

  function destroyFeaturedAuthorities(section) {
    if (!section) return;

    if (section._featuredAuthoritiesResizeObserver) {
      section._featuredAuthoritiesResizeObserver.disconnect();
      section._featuredAuthoritiesResizeObserver = null;
    }

    section._featuredAuthoritiesRebuild = null;
    section.dataset.featuredAuthoritiesInit = 'false';
  }

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        callback,
        {
          once: true
        }
      );
    } else {
      callback();
    }
  }

  ready(function () {
    initAllFeaturedAuthorities(document);
  });

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initAllFeaturedAuthorities(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    function (event) {
      var section = null;

      if (
        event.target.matches &&
        event.target.matches(
          '[data-featured-authorities]'
        )
      ) {
        section = event.target;
      } else {
        section = event.target.querySelector(
          '[data-featured-authorities]'
        );
      }

      if (section) {
        destroyFeaturedAuthorities(section);
      }
    }
  );
})();